import { Shape, ShapeType } from '@/services/shape';
import { ResourceKey } from '@/services/shape/attributes';
import { useImagesStore } from '@/store/use-images-store';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { loadImageDom } from '@/utils/fs/image.utils';

interface TransformOption {
  scale?: number;
  alpha?: number;
}

const transformImage = async (dataUrl: string, options: TransformOption) => {
  const imgDom = await loadImageDom(dataUrl);
  if (!imgDom) return;

  const { scale = 1, alpha = 1 } = options;
  const width = imgDom.width * scale;
  const height = imgDom.height * scale;

  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  // 使用 Math.ceil 确保尺寸向上取整，避免浮点数导致的像素丢失
  // 这样可以确保所有像素都被包含，不会出现底部被裁剪的问题
  [canvas.width, canvas.height] = [Math.ceil(width), Math.ceil(height)];
  ctx.globalAlpha = alpha;
  ctx.drawImage(imgDom, 0, 0, width, height);

  return canvas.toDataURL();
};

export const toTranformedBasenames = (
  basenames: string[],
  options: TransformOption
) => {
  const { scale = 1, alpha = 1 } = options;
  const res = [...basenames];
  res.forEach((basename, index) => {
    // 使用lastIndexOf来获取最后一个点的位置
    const lastDotIndex = basename?.lastIndexOf('.');
    if (lastDotIndex === -1) return;

    const name = basename.slice(0, lastDotIndex);
    const ext = basename.slice(lastDotIndex + 1);
    if (!name || !ext) return;

    const _scale = scale.toString().replace('.', '_');
    const _alpha = alpha.toString().replace('.', '_');
    let _name = name;
    if (Number(scale) != 1) {
      _name += `_s${_scale}`;
    }
    if (Number(alpha) != 1) {
      _name += `_a${_alpha}`;
    }
    // 如果有其他的可以在这支持处理

    const _basename = `${_name}.${ext}`;

    // 修改
    res[index] = _basename;
  });
  return res;
};

/**
 * 用于图片变化：缩放，透明度、旋转等；
 * 目前仅支持缩放
 * @param basenames
 * @param options
 * @returns
 */
export const useShapeTransform = () => {
  const imageStore = useImagesStore();
  const layerStore = useLayerStore();

  const _generateShapeTansformedImage = async (
    shape: Shape,
    options: TransformOption
  ) => {
    const imagesMap = imageStore.imagesMap;
    // 首先处理图片资源
    const resources = shape.attrs.resources ?? {};
    const resourceNames = Object.keys(resources) as ResourceKey[];
    const { scale = 1 } = options;
    for (const resourceName of resourceNames) {
      const resourceItem = resources[resourceName];
      if (!resourceItem) continue;
      const colorIds = Object.keys(resourceItem);
      for (const colorId of colorIds) {
        const basenames = resourceItem[colorId];
        if (!basenames.length) continue;
        // 将所有图片资源进行缩放，然后保存
        for (const basename of basenames) {
          if (!imagesMap[basename]) continue;
          // 跳过已生成的变换图片，避免重复处理
          const targetBasename = toTranformedBasenames([basename], options)?.[0];
          if (imagesMap[targetBasename]) continue;
          const { value: base64, width, height } = imagesMap[basename];
          if (!base64) continue;
          const newBase64 = await transformImage(base64, options);
          if (!newBase64) continue;
          imageStore.insertImage({
            basename: targetBasename,
            value: newBase64,
            width: Math.round(width * scale),
            height: Math.round(height * scale),
            realpath: ''
          });
        }
      }
    }
  };

  const generateTransformedImages = async () => {
    for (const theme of layerStore.themes) {
      const themeScale = theme.scale ?? 1;
      const stack = theme.children.map((layer) => Shape.getShape(layer.id));
      while (stack.length) {
        const shape = stack.shift();
        if (!shape) continue;
        if (shape?.children.length) stack.push(...shape.children);

        // 计算最终缩放：全局缩放 × 元素自身缩放（仅图片和图组受全局缩放影响）
        const shapeScale = shape.attrs.scale ?? 1;
        const effectiveScale = canuseScale(shape) ? themeScale * shapeScale : shapeScale;

        // 生成所有变化的图片
        if (
          (canuseScale(shape) && effectiveScale != 1) ||
          shape.attrs.alpha != 1
        ) {
          await _generateShapeTansformedImage(shape, {
            scale: effectiveScale,
            alpha: shape.attrs.alpha ?? 1
          });
        }
      }
    }
  };

  /**
   *  设置图形的缩放
   * @param shape
   * @param scale  缩放比例,between 0-1.5
   * @param scaleInfo 设置是否操作了单个缩放
   * @returns
   */
  const setScale = async (shape: Shape, scale: number, scaleInfo: {isSetSingleScale?: boolean} = {}) => {
    // scale 必须在0-1之间
    if (scale < scaleMaxMin.min || scale > scaleMaxMin.max) return;
    shape.setAttrs({
      scale,
      ...scaleInfo
    });
  };

  const scaleMaxMin = {
    min: 0.3,
    max: 1.3
  };

  const canuseScale = (shape: Shape) => {
    const shapeType = shape.type;
    return (
      shapeType == ShapeType.ImageArray ||
      (shapeType == ShapeType.Image && !shape.attrs.source)
    );
  };

  const alphaMaxMin = {
    min: 0,
    max: 1
  };

  /**
   *  设置图形的透明度
   * @param shape
   * @param alpha  透明度比例,between 0（完全透明）-1（不透明）
   * @returns
   */
  const setAlpha = async (shape: Shape, alpha: number) => {
    // alpha 必须在0-1之间
    if (alpha < alphaMaxMin.min || alpha > alphaMaxMin.max) return;
    shape.setAttrs({
      alpha
    });
  };

  const canuseAlpha = (shape: Shape) => {
    const shapeType = shape.type;
    return (
      (shapeType == ShapeType.Image && !shape.attrs.source) ||
      shapeType == ShapeType.ImageArray ||
      shapeType == ShapeType.ProgressBar ||
      shapeType == ShapeType.PhotoAlbumBg
    );
  };

  // 上表后若图片分辨率大于/等于表盘，会导致显示错误，这种情况下暂时禁用透明度调节
  const disableAlpha = (size: { w: number; h: number }) => {
    const watchfaceStore = useWatchfaceStore();
    const { w = 0, h = 0 } = size;
    const { width, height } = watchfaceStore.size;
    return w >= width || h >= height;
  };

  return {
    setScale,
    canuseScale,
    generateTransformedImages,
    scaleMaxMin,
    setAlpha,
    canuseAlpha,
    disableAlpha
  };
};
